import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { toast } from "react-toastify";
import { Dropdown } from "react-bootstrap";
import { faPencilAlt, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import commonStyles from "../../../lib/common/common.module.css";
import { getPartnerSubmissions, exportPartnerSubmissions, sendManualESIMEmail } from "../api";
import styles from "../styles/cms.module.css";
import { CMS_TYPE, LOCAL_CONSTANTS } from "../../../lib/constants";
import { CMSTypes } from "../../../interfaces";
import { CONSTANTS } from "../../../lib/constants/messages";
import { ActionButton, DropDownMenu, Pagination, Popup } from "../../../ui";
import CreateUpdateFAQPopup from "../../../ui/Popup/createUpdateFAQ";
import { IMAGES } from "../../../assets/images";
import CreateUpdateBlog from "../../../ui/Popup/createUpdateBlog";
import { useParams } from "react-router-dom";
import CreateUpdateInsta from "../../../ui/Popup/createUpdateInsta";
import EmailModal from "./EmailModal";

interface Props {
  cms: CMSTypes.SpecificCMS;
  cmsType: number;
  resetCMS: () => void;
}

const PartnerCode = () => {
  const [faqLoading, setFAQLoading] = useState<boolean>(true);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [faqs, setFaqs] = useState<{ list: any, count: number }>({ list: [], count: 0 });
  const [pagination, setPagination] = useState<{ skip: number, limit: number }>({
    skip: 0,
    limit: LOCAL_CONSTANTS.limitCount,
  });
  const [error, setError] = useState<string>("");
  const [filters, setFilters] = useState({
    partnerCode: "",
    email: "",
    startDate: "",
    endDate: ""
  });
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFAQLoading(true);
    setPagination({
      skip: 0,
      limit: LOCAL_CONSTANTS.limitCount,
    });
    getFaqs();
  };

  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      // Create query object with only non-empty filters
      const query: any = {};
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          query[key] = value;
        }
      });
      
      const response = await exportPartnerSubmissions(query);
      
      // Get CSV content from response
      let csvContent = '';
      if (typeof response === 'string') {
        csvContent = response;
      } else if (typeof response.data === 'string') {
        csvContent = response.data;
      } else {
        throw new Error('Unexpected response format');
      }

      // Verify we have content
      if (!csvContent) {
        throw new Error('No CSV content received');
      }
      
      // Create blob with the CSV string
      const blob = new Blob([csvContent], { 
        type: 'text/csv;charset=utf-8'
      });
      
      // Download file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `partner-submissions-${moment().format('YYYY-MM-DD')}.csv`;
      
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully');
    } catch (err: any) {
      console.error('Export error:', err);
      toast.error('Failed to export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const getFaqs = () => {
    // Create base query with pagination
    const query: any = {
      page: (1 + pagination.skip),
      limit: pagination.limit,
    };

    // Only add non-empty filter values
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        query[key] = value;
      }
    });
    
    getPartnerSubmissions(query).then(res => {
      setFaqs({ list: res?.data?.submissions, count: res?.data?.totalCount });
      setFAQLoading(false);
      setError(res?.message);
    }).catch(err => {
      setFAQLoading(false);
      setError(err.response.data.message);
    });
  };

  useEffect(() => {
    setFAQLoading(true);
    getFaqs();
  }, [pagination.skip]);

  const handleEmailClick = (user: any) => {
    const formData = user.formData || [];
    const fullName = formData.find((field: any) => field.id === "full_name")?.value || "";
    const email = formData.find((field: any) => field.id === "email")?.value || "";
    const country = formData.find((field: any) => field.id === "country")?.value || "";

    // Set the selected user data with all required fields
    setSelectedUser({
      couponId:user?._id,
      firstName: fullName || "",
      
      email,
      countryName: country,
      qrImageUrl: "",
      iccid: "",
      dataAmount: 0,
      duration: 0,
      planName: ""
    });
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (formData: {
    firstName: string;
    
    email: string;
    qrImageUrl: string;
    iccid: string;
    dataAmount: number;
    duration: number;
    planName: string;
    countryName: string;
    couponId: string;
  }) => {
    try {
      await sendManualESIMEmail(formData);
      toast.success("eSIM email sent successfully!");
      setIsEmailModalOpen(false);
      getFaqs();
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error("Failed to send eSIM email. Please try again.");
    }
  };

  return (
    <div>
      <div className={styles.tabDtahd}>
        <h4>Partner</h4>
        <button 
          onClick={handleExportCSV}
          disabled={exportLoading}
          className={`${styles.exportButton} ${exportLoading ? styles.loading : ''}`}
        >
          {exportLoading ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      <form onSubmit={handleFilterSubmit} className={styles.filterForm}>
        <div className={styles.filterRow}>
          <div className={styles.filterField}>
            <label>Partner Code</label>
            <input
              type="text"
              name="partnerCode"
              value={filters.partnerCode}
              onChange={handleFilterChange}
              placeholder="Search by partner code"
            />
          </div>
          <div className={styles.filterField}>
            <label>Email</label>
            <input
              type="text"
              name="email"
              value={filters.email}
              onChange={handleFilterChange}
              placeholder="Search by email"
            />
          </div>
          <div className={styles.filterField}>
            <label>Start Date</label>
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </div>
          <div className={styles.filterField}>
            <label>End Date</label>
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </div>
          <button type="submit" className={styles.filterButton}>
            Apply Filters
          </button>
        </div>
      </form>

      <div className={commonStyles.tableData}>
        <div className={commonStyles.tableData}>
          <table>
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Email</th>
                <th>Travel Start Date</th>
                <th>Travel End Date</th>
                <th>Country</th>
                <th>Phone</th>
                <th>Partner Code</th>
                <th>Source</th>
                <th>Email Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {faqLoading && (
                <tr>
                  <td colSpan={9} align="center" className={styles.loadingText}>
                    {LOCAL_CONSTANTS.loading}
                  </td>
                </tr>
              )}
              {!faqLoading && faqs?.list?.length === 0 && (
                <tr>
                  <td colSpan={9} align="center">
                    No Users Found
                  </td>
                </tr>
              )}
              {!faqLoading &&
                faqs?.list?.map((item: any, index: number) => {
                  const formData = item?.formData || [];
                  const fullName = formData.find((field: any) => field.id === "full_name")?.value || "-";
                  const email = formData.find((field: any) => field.id === "email")?.value || "-";
                  const startDate = formData.find((field: any) => field.id === "start_date")?.value || "";
                  const endDate = formData.find((field: any) => field.id === "end_date")?.value || "";
                  const country = formData.find((field: any) => field.id === "country")?.value || "-";
                  const phone = formData.find((field: any) => field.id === "phone_number")?.value || "-";
                  const partnerCode = item?.partnerCode || "-";
                  const source = item?.source || "-";

                  return (
                    <tr key={item._id}>
                      <td>{fullName}</td>
                      <td>{email}</td>
                      <td>{startDate ? moment(startDate).format('DD-MM-YYYY') : '-'}</td>
                      <td>{endDate ? moment(endDate).format('DD-MM-YYYY') : '-'}</td>
                      <td>{country}</td>
                      <td>{phone}</td>
                      <td>{partnerCode}</td>
                      <td>{source}</td>
                      <td>{item?.emailSentStatus===1?"Pending":"Sent"}</td>
                      <td>{item?.createdAt ? moment(item?.createdAt).format('DD-MM-YYYY') : '-'}</td>
                      <td>
                        <button 
                          className={styles.emailButton}
                          onClick={() => handleEmailClick(item)}
                          disabled={item?.emailSentStatus !== 1}
                        >
                          Send Email
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
          {faqs.count > faqs?.list?.length &&
            <Pagination
              paginationState={pagination}
              setPaginationState={setPagination}
              count={faqs.count}
            />
          }
        </div>
      </div>

      {selectedUser && (
        <EmailModal
          isOpen={isEmailModalOpen}
          onClose={() => {
            setIsEmailModalOpen(false);
            setSelectedUser(null);
          }}
          onSubmit={handleEmailSubmit}
          initialData={selectedUser}
        />
      )}
    </div>
  );
};

export default PartnerCode;